import React, { useState } from "react";
import {
  Paper,
  Box,
  Typography,
  TextField,
  IconButton,
  Tooltip,
  Grid,
  Button,
  FormControlLabel,
  Switch,
  FormGroup,
  Checkbox,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
} from "@mui/material";
import DeleteIcon from "@mui/icons-material/Delete";
import AddIcon from "@mui/icons-material/Add";

function CustomCheckboxGroupModule({ module, onChange, onDelete, isEditable }) {
  const [settingsOpen, setSettingsOpen] = useState(false);
  const [conditionalOpen, setConditionalOpen] = useState(false);

  const handleLabelChange = (e) => {
    onChange({ ...module, label: e.target.value });
  };

  const updateOption = (index, value) => {
    const newOptions = [...(module.options || [])];
    newOptions[index] = value;
    onChange({ ...module, options: newOptions });
  };

  const handleCorrectResponseChange = (option, isChecked) => {
    let updatedCorrectResponses = module.correctResponses
      ? [...module.correctResponses]
      : [];
    if (isChecked) {
      if (!updatedCorrectResponses.includes(option)) {
        updatedCorrectResponses.push(option);
      }
    } else {
      updatedCorrectResponses = updatedCorrectResponses.filter(
        (resp) => resp !== option,
      );
    }
    onChange({ ...module, correctResponses: updatedCorrectResponses });
  };

  const addOption = () => {
    const newOptions = [
      ...(module.options || []),
      `Option ${module.options ? module.options.length + 1 : 1}`,
    ];
    onChange({ ...module, options: newOptions });
  };

  const removeOption = (index) => {
    const newOptions = [...(module.options || [])];
    newOptions.splice(index, 1);
    onChange({ ...module, options: newOptions });
  };

  const handleRequiredToggle = (e) => {
    onChange({ ...module, required: e.target.checked });
  };

  const handleAdminWorkflowToggle = (e) => {
    onChange({ ...module, approvalRequired: e.target.checked });
  };

  return (
    <Paper elevation={3} sx={{ padding: 2, marginBottom: 2 }}>
      {/* Module Header */}
      <Box sx={{ display: "flex", alignItems: "center", marginBottom: 1 }}>
        <Typography variant="subtitle1" sx={{ flexGrow: 1 }}>
          CUSTOM CHECKBOX GROUP MODULE
        </Typography>
        {isEditable && (
          <Box>
            <Tooltip title="Settings">
              <IconButton
                onClick={() => setSettingsOpen(!settingsOpen)}
                size="small"
              >
                <Typography variant="caption">S</Typography>
              </IconButton>
            </Tooltip>
            <Tooltip title="Conditional Visibility">
              <IconButton
                onClick={() => setConditionalOpen(!conditionalOpen)}
                size="small"
              >
                <Typography variant="caption">C</Typography>
              </IconButton>
            </Tooltip>
            <Tooltip title="Delete Module">
              <IconButton onClick={onDelete} size="small" color="error">
                <DeleteIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          </Box>
        )}
      </Box>

      {/* Module Preview */}
      <Box
        sx={{
          border: "1px solid #e0e0e0",
          borderRadius: 1,
          padding: 2,
          backgroundColor: "#fafafa",
          marginBottom: 1,
        }}
      >
        <Typography variant="subtitle1">
          {module.label || "Custom Checkbox Group"}
        </Typography>
        <FormGroup>
          {(module.options || []).map((option, index) => (
            <FormControlLabel
              key={index}
              control={<Switch disabled />}
              label={option}
            />
          ))}
        </FormGroup>
      </Box>

      {/* Settings Panel */}
      {isEditable && settingsOpen && (
        <Box sx={{ marginTop: 2 }}>
          <TextField
            label="Question Label"
            value={module.label || ""}
            onChange={handleLabelChange}
            fullWidth
            margin="dense"
          />
          <Typography variant="subtitle2" sx={{ marginTop: 2 }}>
            Options:
          </Typography>
          {(module.options || []).map((option, index) => (
            <Grid container spacing={1} alignItems="center" key={index}>
              <Grid item xs={6}>
                <TextField
                  label={`Option ${index + 1}`}
                  value={option}
                  onChange={(e) => updateOption(index, e.target.value)}
                  fullWidth
                  margin="dense"
                />
              </Grid>
              <Grid item xs={3}>
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={
                        module.correctResponses
                          ? module.correctResponses.includes(option)
                          : false
                      }
                      onChange={(e) =>
                        handleCorrectResponseChange(option, e.target.checked)
                      }
                    />
                  }
                  label="Correct"
                />
              </Grid>
              <Grid item xs={3}>
                <IconButton
                  onClick={() => removeOption(index)}
                  size="small"
                  color="error"
                  disabled={(module.options || []).length <= 1}
                >
                  <DeleteIcon fontSize="small" />
                </IconButton>
              </Grid>
            </Grid>
          ))}
          <Button
            variant="text"
            startIcon={<AddIcon />}
            onClick={addOption}
            sx={{ marginTop: 1 }}
          >
            Add Option
          </Button>
          {/* Custom Logic for Incorrect Answers */}
          <FormControlLabel
            control={
              <Switch
                checked={
                  module.customLogic
                    ? module.customLogic.action === "setStatusTo3"
                    : false
                }
                onChange={(e) => {
                  const enabled = e.target.checked;
                  onChange({
                    ...module,
                    customLogic: enabled
                      ? {
                          action: "setStatusTo3",
                          customMessage:
                            module.customLogic?.customMessage || "",
                        }
                      : null,
                  });
                }}
                color="primary"
              />
            }
            label="Custom Incorrect Logic (set status to 3)"
          />
          {module.customLogic &&
            module.customLogic.action === "setStatusTo3" && (
              <TextField
                label="Custom Message"
                value={module.customLogic.customMessage || ""}
                onChange={(e) =>
                  onChange({
                    ...module,
                    customLogic: {
                      ...module.customLogic,
                      customMessage: e.target.value,
                    },
                  })
                }
                fullWidth
                margin="dense"
              />
            )}
          {/* Status Dropdowns */}
          <Grid container spacing={2} sx={{ marginTop: 2 }}>
            <Grid item xs={12} sm={6}>
              <FormControl fullWidth margin="dense">
                <InputLabel id="editable-status-label">
                  Editable when in Status:
                </InputLabel>
                <Select
                  labelId="editable-status-label"
                  value={module.editableWhenStatus || 0}
                  onChange={(e) =>
                    onChange({ ...module, editableWhenStatus: e.target.value })
                  }
                  label="Editable when in Status:"
                >
                  <MenuItem value={0}>0, valid</MenuItem>
                  <MenuItem value={8}>8, Completed</MenuItem>
                  <MenuItem value={3}>3, Awaiting Review</MenuItem>
                  <MenuItem value={2}>2, Requested</MenuItem>
                  <MenuItem value={9}>9, Closed</MenuItem>
                  <MenuItem value={7}>7, Declined</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={6}>
              <FormControl fullWidth margin="dense">
                <InputLabel id="visible-status-label">
                  Visible when in Status:
                </InputLabel>
                <Select
                  labelId="visible-status-label"
                  value={module.visibleWhenStatus || 0}
                  onChange={(e) =>
                    onChange({ ...module, visibleWhenStatus: e.target.value })
                  }
                  label="Visible when in Status:"
                >
                  <MenuItem value={0}>0, valid</MenuItem>
                  <MenuItem value={8}>8, Completed</MenuItem>
                  <MenuItem value={3}>3, Awaiting Review</MenuItem>
                  <MenuItem value={2}>2, Requested</MenuItem>
                  <MenuItem value={9}>9, Closed</MenuItem>
                  <MenuItem value={7}>7, Declined</MenuItem>
                </Select>
              </FormControl>
            </Grid>
          </Grid>
          <Grid container spacing={2} alignItems="center" sx={{ marginTop: 2 }}>
            <Grid item xs={6}>
              <FormControlLabel
                control={
                  <Switch
                    checked={module.required || false}
                    onChange={handleRequiredToggle}
                    color="primary"
                  />
                }
                label="Required"
              />
            </Grid>
            <Grid item xs={6}>
              <FormControlLabel
                control={
                  <Switch
                    checked={module.approvalRequired || false}
                    onChange={handleAdminWorkflowToggle}
                    color="primary"
                  />
                }
                label="Admin Workflow"
              />
            </Grid>
          </Grid>
        </Box>
      )}

      {isEditable && conditionalOpen && (
        <Box sx={{ marginTop: 2 }}>
          <Typography variant="body2">
            Conditional Visibility settings...
          </Typography>
        </Box>
      )}
    </Paper>
  );
}

export default CustomCheckboxGroupModule;
